import { isNull } from 'lodash/fp';
import React, { useCallback, useMemo, useState } from 'react';

import { useAppSelector } from '../../../../hooks/react-redux';
import { formatDate } from '../../../../utils/luxon';
import { getAvailableDocumentNames } from '../../../admin/documents/store';
import { Button } from '../../../shared/button/Button';
import { IconButton } from '../../../shared/button/IconButton';
import { Filter } from '../../../shared/icons';
import { Position } from '../../../shared/modal/PositionModal';
import { Scrollable } from '../../../shared/scrollable/Scrollable';
import { CustomTooltip, InformationTooltip, OverflowTooltip } from '../../../shared/tooltip';
import { WizardTab } from '../../../shared/wizard/WizardTab';
import { PreExecutionFilterOption } from '../../search/store';
import styles from '../Reports.module.scss';
import { DocumentReportTemplate } from '../store';
import { TemplateFilterPreviewModal } from './TemplateFilterPreviewModal';

interface DocumentTemplateProps {
    template: DocumentReportTemplate;
    openTemplate: (reportTemplateId: number) => void;
    openDeleteConfirmationModal: (reportTemplateId: number) => void;
    canMakeTemplateChanges: boolean;
    testId?: string;
}

export const DocumentTemplate: React.FC<DocumentTemplateProps> = ({ template, openTemplate, openDeleteConfirmationModal, testId, canMakeTemplateChanges }) => {
    const [selectedAgreementTypeId, setSelectedAgreementTypeId] = useState<number>(parseInt(Object.keys(template.reportFields)[0]));
    const [filterModalPosition, setFilterModalPosition] = useState<Position | null>(null);
    const { reportTemplateId, modifiedDate, modifiedByName, name, description, reportFields, filters, metaFields } = template;
    const documentNames = useAppSelector(getAvailableDocumentNames);

    const getRelevantDocumentNames = useCallback((reportAgreementTypeId: number) => documentNames.filter(({ agreementTypeId }) => agreementTypeId === reportAgreementTypeId), [documentNames]);

    const agreementTypes = useMemo(() => Object.entries(reportFields).map(([agreementTypeIdString, agreementTypeReportFields]) => {
        const agreementTypeId = parseInt(agreementTypeIdString);
        const relevantDocumentNames = getRelevantDocumentNames(agreementTypeId);
        const agreementType = relevantDocumentNames[0]?.agreementType || '';
        const documentNames = relevantDocumentNames.map(({ documentName }) => documentName);
        return { agreementType, documentNames, agreementTypeReportFields, select: () => setSelectedAgreementTypeId(agreementTypeId), agreementTypeId, isSelected: agreementTypeId === selectedAgreementTypeId };
    }), [reportFields, getRelevantDocumentNames, selectedAgreementTypeId]);

    const agreementTypeTabs = useMemo(() => agreementTypes.map(({ agreementType, select, isSelected }) => ({
        label: agreementType,
        selected: isSelected,
        onClick: select
    })), [agreementTypes]);

    const getHeaderLabel = useCallback((label: string, fieldName: string, customLabel: string, includeDatasetTitle: boolean) => {
        const columnLabel = customLabel || label;
        return `${columnLabel}${includeDatasetTitle && fieldName.length > 0 ? ` (${fieldName})` : ''}`;
    }, []);

    const selectedDocumentNames = useMemo(() => agreementTypes.find(({ isSelected }) => isSelected)?.documentNames || [], [agreementTypes]);
    const selectedReportFields = useMemo(() => agreementTypes.find(({ isSelected }) => isSelected)?.agreementTypeReportFields.map(({ label, customLabel, includeDatasetTitle, fieldName }) => getHeaderLabel(label, fieldName, customLabel, includeDatasetTitle)) || [], [agreementTypes, getHeaderLabel]);

    const previewFields = useMemo(() => [...metaFields, ...selectedReportFields], [metaFields, selectedReportFields]);

    const templateHasFilters = useMemo(() => !isNull(filters.entityId) || filters.includePreExecution !== PreExecutionFilterOption.EXCLUDE || !!filters.includePreferredTerms || filters.includeRiskScore, [filters]);

    const emptyFiltersTooltip = useMemo(() => !templateHasFilters ? 'No filters were added to this template' : null, [templateHasFilters]);

    const openFiltersModal = useCallback((e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => templateHasFilters && setFilterModalPosition({ x: e.clientX, y: e.clientY }), [templateHasFilters]);
    const closeFiltersModal = useCallback(() => setFilterModalPosition(null), []);

    return (
        <div className={styles.templateWrapper} onDoubleClick={() => openTemplate(reportTemplateId)} data-testid={`${testId}-template-wrapper`}>
            <div className={styles.templateHeader}>
                <div className={styles.templateHeaderColumn}>
                    <div className={styles.templateTitle}>
                        <div className={styles.templateName} data-testid={`${testId}-template-title`}>{name}</div>
                        {description && <InformationTooltip content={description} />}
                    </div>
                </div>
                <div className={styles.templateHeaderColumn}>
                    <div className={styles.templateModifiedBy} data-testid={`${testId}-template-modified-by`}>Last Modified By: {modifiedByName}</div>
                    <div className={styles.templateModifiedDate} data-testid={`${testId}-template-modified-date`}>Last Modified On: {formatDate(modifiedDate)}</div>
                </div>
            </div>
            <div className={styles.templateDocumentNames}>{selectedDocumentNames.join(' - ')}</div>
            <div className={styles.templateFieldsContent} data-testid={`${testId}-template-fields-content`}>
                <div className={styles.templateFieldsLabel}>Preview</div>
                {agreementTypeTabs.length > 1 && <div className={styles.agreementTypeTabWrapper}>
                    {agreementTypeTabs.map(({ label, onClick, selected }) => (
                        <WizardTab key={label} label={label} onClick={onClick} selected={selected} />
                    ))}
                </div>}
                <Scrollable>
                    <div className={styles.templatePreviewWrapper}>
                        {previewFields.map((columnHeader, index) => (
                            <div key={index} className={styles.previewColumnWrapper}>
                                <div className={styles.previewColumnHeader}><OverflowTooltip overlayText={columnHeader} /></div>
                                <div className={styles.previewColumnCell} />
                                <div className={styles.secondRowPreviewColumnCell} />
                            </div>
                        ))}
                    </div>
                </Scrollable>
            </div>
            <div className={styles.templateFooterWrapper}>
                <CustomTooltip overlayText={emptyFiltersTooltip}>
                    <div className={styles.filterPreviewWrapper}>
                        <div className={styles.filterPreviewLabel}>Filters</div>
                        <IconButton icon={Filter} showIndicator={templateHasFilters} onClick={openFiltersModal} fontSize={22} />
                    </div>
                </CustomTooltip>
                <div className={styles.templateButtonWrapper}>
                    <Button label='Delete' onClick={() => openDeleteConfirmationModal(reportTemplateId)} testId={`${testId}-template-delete`} disabled={!canMakeTemplateChanges} />
                    <Button label='Open' onClick={() => openTemplate(reportTemplateId)} testId={`${testId}-template-open`} />
                </div>
            </div>
            <TemplateFilterPreviewModal isOpen={!isNull(filterModalPosition)} closeModal={closeFiltersModal} filters={filters} templateName={name} position={filterModalPosition} />
        </div>
    );
};
